import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

/**
 * Counted-breath phases: NAUI's six recovery breaths, and purge breathing.
 *
 * These count UP, because the question is "have I done all six" / "am I at my
 * limit of five" — not "how long is left". The recovery breaths are mandatory
 * after every hold, so this phase cannot be skipped, only aborted with the
 * session.
 */

interface BreathCounterProps {
  label: string;
  count: number;
  target: number;
  /** Instruction for the current breath. */
  cue: string;
  /** Shown for purge, where the target is a safety cap rather than a goal. */
  capWarning?: string;
  /** Informational context — why this phase matters. Rendered as info, not warning. */
  note?: string;
  onBreath: () => void;
}

const BreathCounter: React.FC<BreathCounterProps> = ({
  label,
  count,
  target,
  cue,
  capWarning,
  note,
  onBreath,
}) => (
  <Box sx={{ textAlign: 'center', py: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.15em' }}>
      {label}
    </Typography>

    <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
      {count} <Typography component="span" variant="h4" color="text.secondary">of {target}</Typography>
    </Typography>

    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
      {cue}
    </Typography>

    {/* Dots rather than a bar: at six items the discrete count is easier to read
        at a glance than a percentage. */}
    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
      {Array.from({ length: target }, (_, i) => (
        <Box
          key={i}
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: i < count ? 'primary.main' : 'action.disabledBackground',
            transition: 'background-color 0.2s',
          }}
        />
      ))}
    </Stack>

    <Button
      variant="contained"
      size="large"
      onClick={onBreath}
      sx={{ mt: 3, minWidth: 200 }}
    >
      Breath done
    </Button>

    {note && (
      <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
        {note}
      </Alert>
    )}

    {capWarning && (
      <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
        {capWarning}
      </Alert>
    )}
  </Box>
);

export default BreathCounter;
